(() => {
  'use strict';

  const GRID_SELECTOR = '#product-grid';
  const FORM_SELECTOR = `${GRID_SELECTOR} .premium-card__form`;
  const GIFT_WRAP_FOR_PROPERTY = '_KB_GiftWrapFor';
  const controllers = new WeakMap();
  let currentCart = null;
  let cartRequest = null;
  let initializeFrame = 0;

  const rootUrl = () => {
    const root = String(window.Shopify?.routes?.root || '/');
    return root.endsWith('/') ? root : `${root}/`;
  };

  const endpoint = (path) => `${rootUrl()}${String(path || '').replace(/^\//, '')}`;

  const trashIcon = () => `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4.5 7h15M9 7V4.8h6V7m-8.5 0 .8 12h9.4l.8-12M10 10.5v5.5M14 10.5v5.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"></path>
    </svg>`;

  const minusIcon = () => `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 12h12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
    </svg>`;

  const plusIcon = () => `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 6v12M6 12h12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path>
    </svg>`;

  const isGiftWrapItem = (item) => {
    const handle = String(item?.handle || '').toLowerCase();
    return Boolean(item?.properties?.[GIFT_WRAP_FOR_PROPERTY]) || handle === 'giftwrap' || handle === 'gift-wrap';
  };

  const matchingLines = (cart, variantId) => {
    if (!Array.isArray(cart?.items)) return [];
    return cart.items.filter(
      (item) => !isGiftWrapItem(item) && Number(item.variant_id) === Number(variantId)
    );
  };

  const visiblePropertyCount = (item) => {
    const properties = item?.properties && typeof item.properties === 'object' ? item.properties : {};
    return Object.keys(properties).filter((key) => key && !key.startsWith('_') && properties[key] !== '').length;
  };

  const preferredLine = (lines) => {
    if (!lines.length) return null;
    return [...lines].sort((a, b) => visiblePropertyCount(a) - visiblePropertyCount(b))[0];
  };

  const totalQuantity = (lines) => lines.reduce(
    (sum, line) => sum + Math.max(0, Number(line.quantity || 0)),
    0
  );

  const getControllerProductTitle = (controller) => {
    const title = controller?.card?.querySelector(
      '.premium-card__title, .premium-card__name, .card__heading, h3, h2'
    )?.textContent;
    return String(title || '').replace(/\s+/g, ' ').trim() || 'this item';
  };

  const confirmRemoval = (controller) => {
    const confirmHandler = window.KBConfirmCartRemoval;
    const itemTitle = getControllerProductTitle(controller);
    if (typeof confirmHandler === 'function') return confirmHandler({ itemTitle });
    return Promise.resolve(window.confirm(`Remove ${itemTitle} from your cart?`));
  };

  function createController(form) {
    if (!form || controllers.has(form)) return controllers.get(form) || null;

    const variantInput = form.querySelector('input[name="id"]');
    const addButton = form.querySelector('button[type="submit"], input[type="submit"]');
    const variantId = Number(variantInput?.value || 0);
    if (!variantId || !addButton) return null;

    form.classList.add('kb-card-cart-form');
    form.dataset.kbVariantId = String(variantId);

    const selector = document.createElement('div');
    selector.className = 'kb-card-quantity-selector';
    selector.hidden = true;
    selector.setAttribute('role', 'group');
    selector.setAttribute('aria-label', 'Cart quantity');
    selector.innerHTML = `
      <button type="button" class="kb-card-quantity__action kb-card-quantity__action--decrease" data-kb-card-decrease aria-label="Decrease quantity">
        ${minusIcon()}
      </button>
      <span class="kb-card-quantity__value-wrap" aria-live="polite">
        <strong class="kb-card-quantity__value" data-kb-card-quantity-value>0</strong>
        <span class="kb-card-quantity__caption">In cart</span>
      </span>
      <button type="button" class="kb-card-quantity__action kb-card-quantity__action--increase" data-kb-card-increase aria-label="Increase quantity">
        ${plusIcon()}
      </button>`;
    form.appendChild(selector);

    const controller = {
      form,
      variantId,
      addButton,
      selector,
      decreaseButton: selector.querySelector('[data-kb-card-decrease]'),
      increaseButton: selector.querySelector('[data-kb-card-increase]'),
      valueElement: selector.querySelector('[data-kb-card-quantity-value]'),
      card: form.closest('.premium-card'),
      lines: [],
      quantity: 0,
      pending: false,
    };

    controllers.set(form, controller);
    return controller;
  }

  function setPending(controller, pending) {
    controller.pending = Boolean(pending);
    controller.selector.classList.toggle('is-loading', controller.pending);
    controller.decreaseButton.disabled = controller.pending;
    controller.increaseButton.disabled = controller.pending;
    controller.selector.setAttribute('aria-busy', controller.pending ? 'true' : 'false');
  }

  function renderController(controller, cart) {
    if (!controller) return;

    const lines = matchingLines(cart, controller.variantId);
    const quantity = totalQuantity(lines);
    controller.lines = lines;
    controller.quantity = quantity;

    const inCart = quantity > 0;
    controller.addButton.hidden = inCart;
    controller.addButton.setAttribute('aria-hidden', inCart ? 'true' : 'false');
    controller.addButton.tabIndex = inCart ? -1 : 0;
    controller.selector.hidden = !inCart;
    controller.card?.classList.toggle('is-kb-in-cart', inCart);

    if (!inCart) {
      controller.valueElement.textContent = '0';
      return;
    }

    controller.valueElement.textContent = String(quantity);
    const isDelete = quantity === 1;
    controller.decreaseButton.classList.toggle('is-delete', isDelete);
    controller.decreaseButton.innerHTML = isDelete ? trashIcon() : minusIcon();
    controller.decreaseButton.setAttribute(
      'aria-label',
      isDelete ? 'Remove item from cart' : 'Decrease quantity'
    );
    controller.increaseButton.setAttribute('aria-label', 'Increase quantity');
  }

  function renderAll(cart) {
    if (!cart) return;
    currentCart = cart;
    document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
      const controller = createController(form);
      renderController(controller, cart);
    });
  }

  async function loadCart({ force = false } = {}) {
    if (cartRequest && !force) return cartRequest;

    cartRequest = fetch(endpoint('cart.js'), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error('Unable to read cart quantities.');
        return response.json();
      })
      .then((cart) => {
        renderAll(cart);
        return cart;
      })
      .catch((error) => {
        console.error('[Kitchen Bazaar] Collection quantity sync failed.', error);
        return null;
      })
      .finally(() => {
        cartRequest = null;
      });

    return cartRequest;
  }

  function lineForDecrease(controller) {
    if (!controller.lines.length) return null;
    const preferred = preferredLine(controller.lines);
    if (preferred && Number(preferred.quantity || 0) > 0) return preferred;
    return controller.lines.find((line) => Number(line.quantity || 0) > 0) || null;
  }

  async function changeQuantity(controller, direction) {
    if (!controller || controller.pending) return;

    const lines = matchingLines(currentCart, controller.variantId);
    const quantity = totalQuantity(lines);
    if (!quantity) {
      await loadCart({ force: true });
      return;
    }

    if (direction === 'decrease' && quantity <= 1) {
      const confirmed = await confirmRemoval(controller);
      if (!confirmed) return;
    }

    let line;
    let nextQuantity;

    if (direction === 'increase') {
      line = preferredLine(lines);
      nextQuantity = Math.max(0, Number(line?.quantity || 0)) + 1;
    } else {
      line = lineForDecrease({ ...controller, lines });
      nextQuantity = Math.max(0, Number(line?.quantity || 0) - 1);
    }

    if (!line?.key) {
      await loadCart({ force: true });
      return;
    }

    setPending(controller, true);

    try {
      const response = await fetch(endpoint('cart/change.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: line.key, quantity: nextQuantity }),
      });
      const cart = await response.json();
      if (!response.ok || cart.status) {
        throw new Error(cart.description || cart.message || 'Unable to update quantity.');
      }
      renderAll(cart);
    } catch (error) {
      console.error('[Kitchen Bazaar] Collection quantity update failed.', error);
      await loadCart({ force: true });
    } finally {
      setPending(controller, false);
    }
  }

  function initializeForms(root = document) {
    if (!root?.querySelectorAll) return;
    root.querySelectorAll(FORM_SELECTOR).forEach(createController);
    if (currentCart) renderAll(currentCart);
  }

  function scheduleInitialize(root = document) {
    window.cancelAnimationFrame(initializeFrame);
    initializeFrame = window.requestAnimationFrame(() => {
      initializeForms(root);
      loadCart().catch(() => {});
    });
  }

  document.addEventListener('click', (event) => {
    const decreaseButton = event.target.closest('[data-kb-card-decrease]');
    const increaseButton = event.target.closest('[data-kb-card-increase]');
    const button = decreaseButton || increaseButton;
    if (!button) return;

    const form = button.closest('.premium-card__form');
    const controller = form ? controllers.get(form) || createController(form) : null;
    if (!controller) return;

    event.preventDefault();
    event.stopPropagation();
    changeQuantity(controller, increaseButton ? 'increase' : 'decrease');
  }, true);

  document.addEventListener('kb:cart:updated', (event) => {
    const cart = event.detail?.cart;
    if (cart) renderAll(cart);
    else loadCart({ force: true }).catch(() => {});
  });

  ['cart:updated', 'cart:change', 'cart:refresh'].forEach((eventName) => {
    document.addEventListener(eventName, () => loadCart({ force: true }).catch(() => {}));
  });

  async function addCollectionItem(form, submitter) {
    const controller = controllers.get(form) || createController(form);
    if (!controller || controller.pending) return;

    const button = submitter || controller.addButton;
    controller.pending = true;
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');

    try {
      const response = await fetch(endpoint('cart/add.js'), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-KB-Custom-Cart-Managed': '1',
        },
        body: new FormData(form),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add this item.');
      }

      const cart = await loadCart({ force: true });
      const customCart = document.querySelector('kb-custom-cart');
      customCart?.setActiveElement?.(button);
      customCart?.requestRefresh?.({ open: true, toast: true, announce: true });

      if (!customCart && cart) {
        document.dispatchEvent(new CustomEvent('kb:cart:updated', { detail: { cart } }));
      }
    } catch (error) {
      console.error('[Kitchen Bazaar] Collection add to cart failed.', error);
      const customCart = document.querySelector('kb-custom-cart');
      customCart?.announce?.(error.message || 'Unable to add this item.');
      await loadCart({ force: true });
    } finally {
      controller.pending = false;
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading');
    }
  }

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form?.matches?.(FORM_SELECTOR)) return;

    /*
     * Own collection-card submissions at capture time. This permanently prevents
     * the browser's native /cart/add redirect while preserving the custom toast,
     * desktop drawer and quantity-selector synchronization.
     */
    event.preventDefault();
    addCollectionItem(form, event.submitter).catch(() => {});
  }, true);

  document.addEventListener('shopify:section:load', (event) => scheduleInitialize(event.target || document));
  window.addEventListener('pageshow', () => loadCart({ force: true }).catch(() => {}));

  const observer = new MutationObserver((mutations) => {
    const hasNewForms = mutations.some((mutation) =>
      Array.from(mutation.addedNodes || []).some((node) =>
        node?.nodeType === 1 && (
          node.matches?.(FORM_SELECTOR) || node.querySelector?.(FORM_SELECTOR)
        )
      )
    );
    if (hasNewForms) scheduleInitialize(document);
  });

  function start() {
    initializeForms(document);
    loadCart({ force: true }).catch(() => {});
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
